/* eslint-disable no-console */
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import * as Sentry from '@sentry/angular';
import type * as SpeechSDKType from 'microsoft-cognitiveservices-speech-sdk';
import { environment } from '../../../../../../environments/environment';
import { SpeechToTextApiKeyServiceInterface, SpeechToTextServiceInterface } from '../prompt-input-speech-to-text.interfaces';
import { Tokens } from '../prompt-input-speech-to-text.provider';

let loadPromise: Promise<typeof SpeechSDKType> | null = null;

export function loadSpeechSdk(): Promise<typeof SpeechSDKType> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Speech SDK can only be loaded in the browser'));
      return;
    }

    // If already present (e.g., loaded earlier), resolve immediately
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    const existing = (window as any).SpeechSDK as typeof SpeechSDKType | undefined;
    if (existing) {
      resolve(existing);
      return;
    }

    const script = document.createElement('script');
    script.src = '/assets/microsoft.cognitiveservices.speech.sdk.bundle-min.js';
    script.async = true;
    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
      const sdk = (window as any).SpeechSDK as typeof SpeechSDKType | undefined;
      sdk ? resolve(sdk) : reject(new Error('SpeechSDK global not found after script load'));
    };
    script.onerror = () => reject(new Error('Failed to load Speech SDK script'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

enum ContinuousRecordingState {
  NotStarted,
  Started,
  StopCalled,
}

@Injectable()
export class SpeechToTextAzureService implements SpeechToTextServiceInterface, OnDestroy {
  private readonly azureApiKeyService = inject<SpeechToTextApiKeyServiceInterface>(Tokens.SpeechToTextApiKeyService);

  public isRecording = signal(false);
  public transcription = signal('');
  public isSpeaking = signal(false);
  public isReady = signal(false);
  public isProcessing = signal(false);

  private tempTranscription = '';
  private finalTranscription = '';
  private recognizer!: SpeechSDKType.SpeechRecognizer;

  // Audio detection
  private audioContext!: AudioContext;
  private mediaStream: MediaStream | null = null;
  private analyser!: AnalyserNode;
  private microphoneStream!: MediaStreamAudioSourceNode;
  private dataArray!: Uint8Array;
  private animationFrameId!: number;

  private continuousRecordingState = ContinuousRecordingState.NotStarted;

  private speechSDK!: typeof SpeechSDKType;

  public async init() {
    this.speechSDK = await loadSpeechSdk();

    this.audioContext = new AudioContext({
      latencyHint: 0.0001,
    });

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 2048;

    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
  }

  public async startRecording() {
    try {
      this.tempTranscription = '';
      this.finalTranscription = '';
      this.transcription.set('');
      this.continuousRecordingState = ContinuousRecordingState.NotStarted;

      await this.initializeRecognizer();

      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Start audio detection
      await this.initializeAudioDetection();
      this.checkVolume();

      // Start continuous recognition
      await this.startContinuousRecognition();
    } catch (error) {
      console.error('Error starting recording:', error);
      this.stopRecording();
      throw error;
    }
  }

  public stopRecording() {
    this.isSpeaking.set(false);
    this.isRecording.set(false);
    this.isProcessing.set(false);
    this.isReady.set(false);
    this.stopAzureRecognizer();
    this.stopAudioDetection();
  }

  // ------------------------------
  // Azure AI
  // ------------------------------
  private async initializeRecognizer() {
    const apiKey = await this.azureApiKeyService.getApiKey();
    const region = environment.azureSpeechToTextRegion;

    if (!region) {
      throw new Error('Region for Azure Speech-to-text not defined');
    }

    const speechConfig = this.speechSDK.SpeechConfig.fromAuthorizationToken(apiKey, region);
    const audioConfig = this.speechSDK.AudioConfig.fromDefaultMicrophoneInput();
    const languageConfig = this.speechSDK.AutoDetectSourceLanguageConfig.fromLanguages(['en-US', 'en-CA', 'fr-CA']);
    speechConfig.speechRecognitionLanguage = 'en-US';

    this.recognizer = this.speechSDK.SpeechRecognizer.FromConfig(speechConfig, languageConfig, audioConfig);

    this.recognizer.recognizing = this.handleRecognizing.bind(this);
    this.recognizer.recognized = this.handleRecognized.bind(this);
    this.recognizer.canceled = this.handleCanceled.bind(this);
    this.recognizer.sessionStopped = this.handleSessionStopped.bind(this);
  }

  private readonly handleRecognizing = (
    recognizer: SpeechSDKType.Recognizer,
    recognizingEvent: SpeechSDKType.SpeechRecognitionEventArgs,
  ) => {
    this.isProcessing.set(true);
    if (recognizingEvent.result.reason === this.speechSDK.ResultReason.RecognizingSpeech) {
      this.tempTranscription = recognizingEvent.result.text;
      this.transcription.set(this.finalTranscription + this.tempTranscription);
    }
  };

  private readonly handleRecognized = (recognizer: SpeechSDKType.Recognizer, recognizedEvent: SpeechSDKType.SpeechRecognitionEventArgs) => {
    if (recognizedEvent.result.reason === this.speechSDK.ResultReason.RecognizedSpeech) {
      const newFinalTranscription = recognizedEvent.result.text;

      if (newFinalTranscription) {
        this.finalTranscription += newFinalTranscription + ' ';
        this.transcription.set(this.finalTranscription);
        this.tempTranscription = '';
      }
    } else if (recognizedEvent.result.reason === this.speechSDK.ResultReason.NoMatch) {
      console.log('No speech recognized.');
    }
    this.isProcessing.set(false);
  };

  /**
   * If this is called, it means an error occurred during recognition.
   */
  private readonly handleCanceled = (
    recognizer: SpeechSDKType.Recognizer,
    canceledEvent: SpeechSDKType.SpeechRecognitionCanceledEventArgs,
  ) => {
    Sentry.captureEvent({
      message: 'Azure speech-to-text recognition canceled',
      level: 'error',
      extra: {
        reason: this.speechSDK.CancellationReason[canceledEvent.reason],
        errorCode: this.speechSDK.CancellationErrorCode[canceledEvent.errorCode],
        errorDetails: canceledEvent.errorDetails,
      },
    });
    this.stopRecording();
  };

  private readonly handleSessionStopped = () => {
    console.log('Session stopped.');
  };

  private startContinuousRecognition(): Promise<void> {
    console.log('Starting continuous recognition');
    return new Promise((resolve, reject) => {
      if (!this.recognizer) {
        console.error('Recognizer not initialized');
        reject(new Error('Recognizer not initialized'));
        return;
      }

      this.recognizer.startContinuousRecognitionAsync(
        () => {
          if (this.continuousRecordingState === ContinuousRecordingState.StopCalled) {
            reject(new Error('stopContinuousRecognitionAsync finished before startContinuousRecognitionAsync'));
          }

          console.log('Recognition started.');
          this.continuousRecordingState = ContinuousRecordingState.Started;
          this.isRecording.set(true);
          this.isReady.set(true);
          this.isProcessing.set(true);
          resolve();
        },
        (err) => {
          console.error('Error starting recognition:', err);
          this.stopRecording();
          reject(new Error(err));
        },
      );
    });
  }

  private stopAzureRecognizer() {
    if (this.recognizer) {
      this.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log('Recognition stopped.');
          this.finalTranscription = '';
          this.continuousRecordingState = ContinuousRecordingState.StopCalled;
        },
        (err) => {
          console.error('Error stopping recognition:', err);
        },
      );
    }
  }

  // ------------------------------
  // Audio detection
  //
  // @TODO: Move it to a shared service or a shared library to reuse it in other service like Azure Speech-to-text
  //
  // ------------------------------
  private async initializeAudioDetection() {
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    this.microphoneStream = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.microphoneStream.connect(this.analyser);
  }

  private readonly checkVolume = () => {
    this.analyser.getByteTimeDomainData(this.dataArray);
    let sum = 0;
    for (const amplitude of this.dataArray) {
      sum += (amplitude - 128) ** 2;
    }
    const volume = Math.sqrt(sum / this.dataArray.length);

    const threshold = 2; // Play with this value to adjust the sensitivity
    if (volume > threshold) {
      this.isSpeaking.set(true);
    } else {
      this.isSpeaking.set(false);
    }

    this.animationFrameId = requestAnimationFrame(this.checkVolume);
  };

  private stopAudioDetection() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    if (this.microphoneStream) {
      this.microphoneStream.disconnect();
    }

    if (this.analyser) {
      this.analyser.disconnect();
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.mediaStream = null;
    }
  }

  ngOnDestroy() {
    this.stopRecording();

    if (this.recognizer) {
      this.recognizer.close();
    }

    if (this.audioContext) {
      void this.audioContext.close();
    }
  }
}
